import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, Tag, ArrowLeft, ArrowRight } from 'lucide-react';
import { newsArticles } from '../data/mock';
import Layout from '../components/layout/Layout';

const NewsPage = () => {
  const { id } = useParams();

  // Single article view
  if (id) {
    const article = newsArticles.find(a => a.id === parseInt(id));
    
    if (!article) {
      return (
        <Layout>
          <div className="min-h-[60vh] flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-700 mb-4">Artículo no encontrado</h1>
              <Link to="/noticias" className="text-[#1E40AF] hover:underline">Volver a noticias</Link>
            </div>
          </div>
        </Layout>
      );
    }

    return (
      <Layout>
        {/* Hero */}
        <section className="relative py-16 bg-[#0A1628]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
              <span>/</span>
              <Link to="/noticias" className="hover:text-white transition-colors">Noticias</Link>
              <span>/</span>
              <span className="text-blue-400 truncate">{article.title}</span>
            </nav>
            <span className="inline-block px-3 py-1 bg-[#1E40AF] text-white text-sm rounded-full mb-4">
              {article.category}
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-gray-400 text-sm">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {article.date}
              </span>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <img
              src={article.image}
              alt={article.title}
              className="w-full aspect-video object-cover rounded-2xl mb-8"
            />
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 leading-relaxed mb-6">
                {article.excerpt}
              </p>
              <p className="text-gray-600 leading-relaxed">
                COPARMEX Manzanillo continúa trabajando para fortalecer el ecosistema empresarial de la región. 
                Este evento representa un paso más en nuestro compromiso con el desarrollo económico y social del puerto.
              </p>
              <p className="text-gray-600 leading-relaxed mt-6">
                Invitamos a todos nuestros socios y a la comunidad empresarial en general a mantenerse informados 
                sobre nuestras actividades y a participar activamente en las iniciativas que promovemos.
              </p>
            </div>

            <div className="mt-12 pt-8 border-t">
              <Link
                to="/noticias"
                className="inline-flex items-center gap-2 text-[#1E40AF] font-medium hover:gap-3 transition-all"
              >
                <ArrowLeft className="h-5 w-5" />
                Volver a noticias
              </Link>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  // News list view
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-[#0A1628] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-blue-400 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
              <span>/</span>
              <span className="text-blue-400">Noticias</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Noticias y Eventos
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Manténte informado sobre las últimas noticias, eventos y actividades de COPARMEX Manzanillo.
            </p>
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured Article */}
          <div className="mb-12">
            <Link
              to={`/noticias/${newsArticles[0].id}`}
              className="group grid md:grid-cols-2 gap-8 bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300"
            >
              <div className="aspect-video md:aspect-auto overflow-hidden">
                <img
                  src={newsArticles[0].image}
                  alt={newsArticles[0].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <span className="inline-block w-fit px-3 py-1 bg-[#1E40AF]/10 text-[#1E40AF] text-sm font-medium rounded-full mb-4">
                  {newsArticles[0].category}
                </span>
                <h2 className="text-2xl font-bold text-[#0A1628] mb-4 group-hover:text-[#1E40AF] transition-colors">
                  {newsArticles[0].title}
                </h2>
                <p className="text-gray-600 mb-4">
                  {newsArticles[0].excerpt}
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-400">
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {newsArticles[0].date}
                  </span>
                </div>
              </div>
            </Link>
          </div>

          {/* News Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {newsArticles.slice(1).map((article) => (
              <Link
                key={article.id}
                to={`/noticias/${article.id}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 hover-lift"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-[#1E40AF]/10 text-[#1E40AF] text-xs font-medium rounded-full mb-3">
                    {article.category}
                  </span>
                  <h3 className="font-semibold text-[#0A1628] mb-2 group-hover:text-[#1E40AF] transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {article.date}
                    </span>
                    <span className="text-[#1E40AF] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      Leer más
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NewsPage;
